/**
 * FormValidator.js
 * -------------------------------
 * Extracted validation logic from ContactModal.js
 * Provides reusable form validation methods for contact forms and other forms.
 * Now uses validation.json as the single source of truth for patterns and rules.
 *
 * Features:
 * - Email validation with RFC-compliant regex from JSON config
 * - Phone number validation with international support from JSON config
 * - Field-specific validation rules loaded from JSON
 * - Comprehensive form validation with detailed error reporting
 * - Static methods for easy testing and reuse
 * - Consistent error message formatting
 * - Dynamic configuration loading for flexibility
 */

export class FormValidator {
  // Static property to hold loaded validation config
  static validationConfig = null;

  // Redesign contact fields (spec §5.13.4), validated when present in a form
  static REDESIGN_FIELDS = ['reason', 'who', 'role', 'method', 'program', 'source', 'ctaSource', 'page'];

  /**
   * Load validation configuration from JSON
   * Call this once at app initialization
   * @param {Object} config - The validation configuration object from validation.json
   */
  static loadConfig(config) {
    if (!config) {
      console.warn('No validation config provided, using defaults');
      return;
    }
    
    this.validationConfig = config;
    
    // Convert pattern strings to RegExp objects
    this.patterns = {};
    if (config.patterns) {
      for (const [key, pattern] of Object.entries(config.patterns)) {
        try {
          this.patterns[key] = new RegExp(pattern);
        } catch (error) {
          console.warn(`Failed to create regex for pattern '${key}':`, error.message);
        }
      }
    }
    
    this.rules = config.rules || {};
    this.contextRules = config.contextSpecificRules || {};
  }

  /**
   * Initialize validator by fetching validation.json
   * @param {string} jsonPath - Path to validation.json file
   * @returns {Promise<boolean>} Success status
   */
  // in FormValidator.js
  // in FormValidator.js
  static async initialize(jsonPath = '/config/contact/validation.json') {
    const tryPaths = [jsonPath, '/data/validation.json'];
    for (const p of tryPaths) {
      try {
        const res = await fetch(p);
        if (res.ok) {
          this.loadConfig(await res.json());
          return true;
        }
      } catch (_) {}
    }
    console.error('Failed to load validation config from known paths, using defaults');
    this.loadConfig(this.getDefaultConfig());
    return false;
  }

  /**
   * Get default configuration as fallback
   * @private
   */
  static getDefaultConfig() {
    return {
      patterns: {
        email: '^[a-zA-Z0-9.!#$%&\'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$',
        phone: '^\\+?[\\d(][\\d\\s\\-().]{6,19}$',
        name: '^[a-zA-Z\\s\\-\']+$'
      },
      rules: {
        firstName: {
          required: true,
          minLength: 2,
          maxLength: 50,
          pattern: 'name',
          patternDescription: 'Only letters, spaces, hyphens, and apostrophes allowed'
        },
        lastName: {
          required: false,
          minLength: 0,
          maxLength: 50,
          pattern: 'name',
          patternDescription: 'Only letters, spaces, hyphens, and apostrophes allowed'
        },
        email: {
          required: true,
          maxLength: 254,
          pattern: 'email',
          patternDescription: 'Must be a valid email address'
        },
        phone: {
          required: false,
          pattern: 'phone',
          patternDescription: 'Must be a valid phone number'
        },
        message: {
          required: false,
          minLength: 10,
          maxLength: 2000,
          patternDescription: 'Message content'
        }
      },
      contextSpecificRules: {}
    };
  }

  /**
   * Get validation rules for a field, optionally with context override
   * @param {string} fieldName - The field name
   * @param {string|null} context - Optional context (e.g., 'careers', 'consultation')
   * @returns {Object} Field rules
   */
  static getFieldRules(fieldName, context = null) {
    if (!this.rules) {
      this.loadConfig(this.getDefaultConfig());
    }
    
    const baseRules = this.rules[fieldName] || {};
    
    // Apply context-specific overrides
    if (context && this.contextRules[context]?.[fieldName]) {
      return {
        ...baseRules,
        ...this.contextRules[context][fieldName]
      };
    }
    
    return baseRules;
  }

  /**
   * Get a specific pattern by name
   * @param {string} patternName - The pattern name
   * @returns {RegExp|null} The regex pattern
   */
  static getPattern(patternName) {
    if (!this.patterns) {
      this.loadConfig(this.getDefaultConfig());
    }
    return this.patterns[patternName] || null;
  }

  /**
   * Validates a complete contact form
   * @param {HTMLFormElement|FormData} form - The form element or FormData object
   * @param {string|null} context - Optional validation context
   * @param {{roleRequired?: boolean, programRequired?: boolean}} [requirements] -
   *   Per-mode requirements for the redesign fields (spec §5.13.4)
   * @returns {Object} Validation result with valid flag, errors array, and message
   */
  static validateContactForm(form, context = null, requirements = {}) {
    const errors = [];
    let valid = true;

    // Extract form data
    const formData = form instanceof FormData ? form : new FormData(form);
    const data = {
      firstName: formData.get('firstName')?.trim() || '',
      lastName: formData.get('lastName')?.trim() || '',
      email: formData.get('email')?.trim() || '',
      phone: formData.get('phone')?.trim() || '',
      message: formData.get('message')?.trim() || ''
    };
    // Redesign fields are checked only when the form renders them. Their
    // option lists are enforced server-side.
    for (const field of this.REDESIGN_FIELDS) {
      if (formData.has(field)) data[field] = String(formData.get(field) || '').trim();
    }
    const requiredByMode = {
      role: requirements.roleRequired === true,
      program: requirements.programRequired === true
    };

    // Validate each field using context-aware rules
    for (const [fieldName, value] of Object.entries(data)) {
      if (requiredByMode[fieldName] && !value) {
        errors.push({
          field: fieldName,
          message: fieldName === 'role' ? 'Please choose your role' : 'Please choose a program'
        });
        valid = false;
        continue;
      }
      const fieldValidation = this.validateField(fieldName, value, context);
      if (!fieldValidation.valid) {
        errors.push(fieldValidation.error);
        valid = false;
      }
    }

    return {
      valid,
      errors,
      message: valid ? '' : 'Please correct the errors below',
      data // Return cleaned data for convenience
    };
  }

  /**
   * Validates a single form field based on its name and value
   * @param {string} fieldName - The name of the field to validate
   * @param {string} value - The value to validate
   * @param {string|null} context - Optional validation context
   * @returns {Object} Validation result with valid flag and error object
   */
  static validateField(fieldName, value, context = null) {
    const trimmedValue = value?.trim() || '';
    const rules = this.getFieldRules(fieldName, context);
    
    if (!rules || Object.keys(rules).length === 0) {
      return { valid: true, error: null };
    }

    // Check required
    if (rules.required && !trimmedValue) {
      return {
        valid: false,
        error: { field: fieldName, message: `${this.formatFieldName(fieldName)} is required` }
      };
    }

    // If field is empty and not required, it's valid
    if (!trimmedValue && !rules.required) {
      return { valid: true, error: null };
    }

    // Check minimum length
    if (rules.minLength && trimmedValue.length < rules.minLength) {
      return {
        valid: false,
        error: { 
          field: fieldName, 
          message: `${this.formatFieldName(fieldName)} must be at least ${rules.minLength} characters` 
        }
      };
    }

    // Check maximum length
    if (rules.maxLength && trimmedValue.length > rules.maxLength) {
      return {
        valid: false,
        error: { 
          field: fieldName, 
          message: `${this.formatFieldName(fieldName)} must be less than ${rules.maxLength} characters` 
        }
      };
    }

    // Check pattern if specified
    if (rules.pattern) {
      const pattern = this.getPattern(rules.pattern);
      if (pattern && !pattern.test(trimmedValue)) {
        return {
          valid: false,
          error: { 
            field: fieldName, 
            message: rules.patternDescription || `${this.formatFieldName(fieldName)} format is invalid` 
          }
        };
      }
    }

    return { valid: true, error: null };
  }

  /**
   * Format field name for display in error messages
   * @param {string} fieldName - The field name
   * @returns {string} Formatted field name
   */
  static formatFieldName(fieldName) {
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  /**
   * Validates first name field (backward compatibility wrapper)
   * @param {string} value - The first name value
   * @param {string|null} context - Optional validation context
   * @returns {Object} Validation result
   */
  static validateFirstName(value, context = null) {
    return this.validateField('firstName', value, context);
  }

  /**
   * Validates last name field (backward compatibility wrapper)
   * @param {string} value - The last name value
   * @param {string|null} context - Optional validation context
   * @returns {Object} Validation result
   */
  static validateLastName(value, context = null) {
    return this.validateField('lastName', value, context);
  }

  /**
   * Validates email field (backward compatibility wrapper)
   * @param {string} value - The email value
   * @param {string|null} context - Optional validation context
   * @returns {Object} Validation result
   */
  static validateEmail(value, context = null) {
    return this.validateField('email', value, context);
  }

  /**
   * Validates phone field (backward compatibility wrapper)
   * @param {string} value - The phone value
   * @param {string|null} context - Optional validation context
   * @returns {Object} Validation result
   */
  static validatePhone(value, context = null) {
    return this.validateField('phone', value, context);
  }

  /**
   * Validates message field (backward compatibility wrapper)
   * @param {string} value - The message value
   * @param {string|null} context - Optional validation context
   * @returns {Object} Validation result
   */
  static validateMessage(value, context = null) {
    return this.validateField('message', value, context);
  }

  /**
   * Validates email format using pattern from config
   * @param {string} email - The email to validate
   * @returns {boolean} True if valid email format
   */
  static isValidEmail(email) {
    const emailPattern = this.getPattern('email');
    return emailPattern ? emailPattern.test(email) : false;
  }

  /**
   * Validates phone number format using pattern from config
   * @param {string} phone - The phone number to validate
   * @returns {boolean} True if valid phone format
   */
  static isValidPhone(phone) {
    const phonePattern = this.getPattern('phone');
    if (!phonePattern) return false;
    
    // Clean phone for validation
    const cleanPhone = phone.replace(/[^\d+\-().]/g, '');
    return phonePattern.test(cleanPhone);
  }

  /**
   * Validates multiple fields at once
   * @param {Object} fields - Object with field names as keys and values as values
   * @param {string|null} context - Optional validation context
   * @returns {Object} Validation result with valid flag and errors array
   */
  static validateFields(fields, context = null) {
    const errors = [];
    let valid = true;

    for (const [fieldName, value] of Object.entries(fields)) {
      const fieldValidation = this.validateField(fieldName, value, context);
      if (!fieldValidation.valid) {
        errors.push(fieldValidation.error);
        valid = false;
      }
    }

    return {
      valid,
      errors,
      message: valid ? '' : 'Please correct the errors below'
    };
  }

  /**
   * Utility method to get all field rules at once
   * @param {string|null} context - Optional validation context
   * @returns {Object} All validation rules
   */
  static getAllFieldRules(context = null) {
    if (!this.rules) {
      this.loadConfig(this.getDefaultConfig());
    }
    
    const allRules = {};
    for (const fieldName of Object.keys(this.rules)) {
      allRules[fieldName] = this.getFieldRules(fieldName, context);
    }
    return allRules;
  }
}

// Auto-initialize with default config
FormValidator.loadConfig(FormValidator.getDefaultConfig());